import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from 'src/app/store.service';
import { UtilitiesService } from 'src/app/core/utilities.service';

@Component({
  selector: 'app-domain-setup',
  templateUrl: './domain-setup.component.html',
  styleUrls: ['./domain-setup.component.scss'],
})
export class DomainSetupComponent implements OnInit {
  @ViewChild('domainDetailsModal') domainDetailsModal: any;
  @ViewChild('domainManageSubdomainsModal') domainManageSubdomainsModal: any;
  @ViewChild('domainAddModal') domainAddModal: any;
  @ViewChild('guideModal001') guideModal001: any;
  @ViewChild('guideModal002') guideModal002: any;

  domainList: any[] = [];
  isDomainListLoading: boolean = false;
  selectedDomain: any = {
    cnameName: '',
    cnameValue: '',
  };
  domainFinalStep = 23;
  userDomainSubdomain: string = '';
  isVerifyingDomainSubdomain: boolean = false;
  deletingDomainIds: { [id: string]: boolean } = {};
  domainModalRef: any;

  // Take Action Step Modals State
  isVerifyingCname: boolean = false;
  stepSubdomainInput: string = '';
  isVerifyingStepSubdomain: boolean = false;

  // Add Domain Modal State (Single Existing Domain Integration Flow)
  addDomainInput: string = '';
  addDomainError: string = '';
  isCheckingAddDomain: boolean = false;
  isSubmittingAddDomain: boolean = false;
  addDomainStatus: 'idle' | 'registered' | 'unregistered' | 'error' = 'idle';
  addDomainCheckedName: string = '';
  isCnameCopied: boolean = false;

  private bearer: string = '';
  private store_id: string = '';

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private svc: StoreService,
    private modalService: NgbModal,
    private utils: UtilitiesService
  ) {}

  ngOnInit(): void {
    this.getDomainList();
  }

  getDomainList() {
    this.refreshDomains(false);
  }

  refreshDomains(showNotification = true) {
    this.bearer = localStorage.getItem('bearer') || '';
    this.store_id = localStorage.getItem('store_id') || '';
    this.isDomainListLoading = true;

    this.svc.getDomainList(this.bearer, this.store_id).subscribe(
      (res) => {
        this.domainList = res && res['data'] ? res['data'] : [];
        this.isDomainListLoading = false;
        if (showNotification) {
          this.utils.notify.showSuccess(this.utils.translate('Domain list refreshed successfully.'));
        }
        this.changeDetectorRef.markForCheck();
      },
      (error) => {
        this.isDomainListLoading = false;
        if (showNotification) {
          this.utils.notify.showError(this.utils.translate('Failed to refresh domain list.'));
        }
        this.changeDetectorRef.markForCheck();
      },
      () => {
        this.isDomainListLoading = false;
        this.changeDetectorRef.markForCheck();
      }
    );
  }

  displayDomainStatus(status: any) {
    switch (+status) {
      case 0:
        return 'Disabled';
      case 1:
        return 'Deploying';
      case 2:
        return 'Active';
      case 3:
        return 'Failed';
      case 8:
        return 'Deleting';
      case 9:
        return 'Deleted';
      default:
        return `Unknown code (${status})`;
    }
  }

  getDomainPercentage(setup_progress: any, finalStep: any) {
    const p = Math.round((+setup_progress / +finalStep) * 100);
    return isNaN(p) ? '0%' : `${p}%`;
  }

  getSubdomainList(data: any): string[] {
    if (!data || !data.subdomains) return [];
    try {
      if (Array.isArray(data.subdomains)) return data.subdomains;
      const parsed = JSON.parse(data.subdomains);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  isClickableSubdomain(sub: string): boolean {
    return !!sub && sub !== '*' && sub !== '@';
  }

  buildSubdomainUrl(sub: string, domainName: string): string {
    if (!sub || !domainName) return '#';
    const cleanSub = sub.trim().replace(/^\.+|\.+$/g, '');
    const cleanDomain = domainName.trim().replace(/^\.+|\.+$/g, '');
    return `https://${cleanSub}.${cleanDomain}`;
  }

  showDomainDetails(data: any) {
    this.selectedDomain = data;
    this.domainModalRef = this.modalService.open(this.domainDetailsModal, {
      size: 'lg',
      backdrop: 'static',
    });
  }

  isDeletingDomain(data: any): boolean {
    if (!data) return false;
    return !!this.deletingDomainIds[data?.store_domain_id] || +data?.status === 8 || +data?.status === 9;
  }

  deleteDomain(data: any) {
    if (!data || this.isDeletingDomain(data)) return;
    this.utils.msgBox.open({
      message: this.utils.translate(
        "Are you sure you want to remove domain '{0}'? This action cannot be undone.",
        [data.domain_name]
      ),
      title: this.utils.translate('Remove Domain'),
      icon: 'warning',
      buttonList: [
        {
          text: this.utils.translate('Cancel'),
        },
        {
          text: this.utils.translate('Remove'),
          style: 'danger',
          onClick: () => {
            const domainId = data.store_domain_id;
            this.deletingDomainIds[domainId] = true;
            this.svc.deleteDomain({ store_domain_id: domainId }).subscribe(
              (res) => {
                this.deletingDomainIds[domainId] = false;
                this.utils.notify.showSuccess(
                  this.utils.translate("Domain '{0}' removed successfully.", [data.domain_name])
                );
                this.getDomainList();
              },
              (err) => {
                this.deletingDomainIds[domainId] = false;
                this.utils.notify.showError(
                  err?.error?.message || this.utils.translate('Failed to remove domain.')
                );
              }
            );
          },
        },
      ],
    });
  }

  openManageSubdomains(domainData: any) {
    if (this.isDeletingDomain(domainData)) return;
    this.selectedDomain = domainData;
    this.userDomainSubdomain = '';
    this.domainModalRef = this.modalService.open(this.domainManageSubdomainsModal, {
      size: 'lg',
      backdrop: 'static',
    });
  }

  closeDomainModal() {
    this.domainModalRef?.close();
    this.domainModalRef = null;
    this.userDomainSubdomain = '';
  }

  proceedVerifySubdomain() {
    const sub = (this.userDomainSubdomain || '').trim().toLowerCase();
    if (!sub) {
      this.utils.msgBox.open({
        message: this.utils.translate('Please enter a subdomain name before adding.'),
        title: this.utils.translate('Subdomain required'),
        icon: 'warning',
      });
      return;
    }

    const currentList = this.getSubdomainList(this.selectedDomain);
    if (currentList.length >= 5) {
      this.utils.notify.showError(this.utils.translate('Maximum 5 subdomains limit reached.'));
      return;
    }

    if (currentList.includes(sub)) {
      this.utils.notify.showError(this.utils.translate("Subdomain '{0}' already exists.", [sub]));
      return;
    }

    const params = {
      store_domain_id: this.selectedDomain.store_domain_id,
      storeId: this.selectedDomain.store_id || this.store_id,
      subdomain: sub,
    };

    const token = localStorage.getItem('bearer') || this.bearer;
    this.isVerifyingDomainSubdomain = true;
    this.svc.requestVerifyDomain(token, params).subscribe(
      (res: any) => {
        this.isVerifyingDomainSubdomain = false;
        if (res && res.status === 'success') {
          this.utils.notify.showSuccess(
            this.utils.translate("Subdomain '{0}' added and verified successfully.", [sub])
          );
          // Optimistically update list in modal
          try {
            const list = this.getSubdomainList(this.selectedDomain);
            if (!list.includes(sub)) {
              list.push(sub);
              this.selectedDomain.subdomains = JSON.stringify(list);
            }
          } catch {}
          this.userDomainSubdomain = '';
          this.getDomainList();
          this.changeDetectorRef.detectChanges();
        } else {
          this.utils.notify.showError(
            res?.message || this.utils.translate('Verification failed. Please ensure the CNAME DNS record is added.')
          );
        }
      },
      (err: any) => {
        this.isVerifyingDomainSubdomain = false;
        this.utils.notify.showError(
          err?.error?.message || this.utils.translate('Failed to verify subdomain. Please check your DNS settings.')
        );
      }
    );
  }

  deleteSubdomain(sub: string) {
    if (!sub) return;
    const domainId = this.selectedDomain?.store_domain_id;
    const storeId = this.selectedDomain?.store_id || this.store_id;
    this.utils.msgBox.open({
      message: this.utils.translate("Are you sure you want to remove subdomain '{0}'?", [sub]),
      title: this.utils.translate('Remove Subdomain'),
      icon: 'warning',
      buttonList: [
        { text: this.utils.translate('Cancel') },
        {
          text: this.utils.translate('Delete'),
          style: 'danger',
          onClick: () => {
            const token = localStorage.getItem('bearer') || this.bearer;
            this.svc.deleteSubdomain(token, { store_id: storeId, store_domain_id: domainId, subdomain: sub }).subscribe(
              () => {
                this.utils.notify.showSuccess(this.utils.translate("Subdomain '{0}' removed.", [sub]));
                // Optimistically remove from modal view
                try {
                  const list = this.getSubdomainList(this.selectedDomain);
                  const updated = list.filter((s) => s !== sub);
                  this.selectedDomain.subdomains = JSON.stringify(updated);
                } catch {}
                this.getDomainList();
                this.changeDetectorRef.detectChanges();
              },
              (err: any) => {
                this.utils.notify.showError(err?.error?.message || this.utils.translate('Failed to delete subdomain.'));
              }
            );
          },
        },
      ],
    });
  }

  openAddDomainModal() {
    this.addDomainInput = '';
    this.addDomainError = '';
    this.isCheckingAddDomain = false;
    this.isSubmittingAddDomain = false;
    this.addDomainStatus = 'idle';
    this.addDomainCheckedName = '';
    this.isCnameCopied = false;
    this.domainModalRef = this.modalService.open(this.domainAddModal, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
    });
  }

  cleanDomainInput(input: string): string {
    if (!input) return '';
    return input
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/.*$/, '')
      .trim();
  }

  isValidDomainFormat(domain: string): boolean {
    if (!domain || domain.length < 3 || domain.length > 253) return false;
    const pattern = /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return pattern.test(domain);
  }

  onAddDomainInputChange() {
    this.addDomainError = '';
    this.addDomainStatus = 'idle';
    this.addDomainCheckedName = '';
  }

  checkExistingDomainAvailability() {
    const raw = this.cleanDomainInput(this.addDomainInput);
    if (!raw) {
      this.addDomainError = this.utils.translate('Please enter your domain name to check.');
      return;
    }
    if (!this.isValidDomainFormat(raw)) {
      this.addDomainError = this.utils.translate('Please enter a valid domain format (e.g. yourstore.com).');
      return;
    }

    this.isCheckingAddDomain = true;
    this.addDomainError = '';
    this.addDomainStatus = 'idle';
    this.addDomainCheckedName = raw;

    const token = localStorage.getItem('bearer') || this.bearer;
    const params = { domain: raw };

    this.svc.validateDomain(token, params).subscribe(
      (res: any) => {
        this.isCheckingAddDomain = false;
        if (res && res['status'] === 'success') {
          // In domain registry check: available !== true means the domain is registered on DNS (ready to connect)
          if (res['data']?.available !== true) {
            this.addDomainStatus = 'registered';
            this.addDomainError = '';
          } else {
            this.addDomainStatus = 'unregistered';
            this.addDomainError = this.utils.translate(
              "This domain is not registered yet with any domain registrar. Make sure you have purchased '{0}' before connecting.",
              [raw]
            );
          }
        } else {
          this.addDomainStatus = 'registered';
        }
        this.changeDetectorRef.detectChanges();
      },
      (err: any) => {
        this.isCheckingAddDomain = false;
        const msg = err?.error?.message;
        if (msg && typeof msg === 'string' && msg.toLowerCase().includes('unique')) {
          this.addDomainStatus = 'error';
          this.addDomainError = this.utils.translate("The domain '{0}' is already connected to an existing store.", [raw]);
        } else {
          this.addDomainStatus = 'registered';
        }
        this.changeDetectorRef.detectChanges();
      }
    );
  }

  submitAddDomain() {
    const raw = this.cleanDomainInput(this.addDomainInput);
    if (!raw) {
      this.addDomainError = this.utils.translate('Please enter a domain name.');
      return;
    }
    if (!this.isValidDomainFormat(raw)) {
      this.addDomainError = this.utils.translate('Please enter a valid domain format (e.g. yourstore.com).');
      return;
    }

    this.isSubmittingAddDomain = true;
    this.addDomainError = '';

    const token = localStorage.getItem('bearer') || this.bearer;
    const store_id = localStorage.getItem('store_id') || this.store_id;

    const params = {
      storeId: store_id,
      customDomain: raw,
    };

    this.svc.createExistingCustomDomain(token, params).subscribe(
      (res: any) => {
        this.isSubmittingAddDomain = false;
        this.utils.notify.showSuccess(
          this.utils.translate("Domain '{0}' connected successfully.", [raw])
        );
        this.closeDomainModal();
        this.getDomainList();
        this.changeDetectorRef.detectChanges();
      },
      (err: any) => {
        this.isSubmittingAddDomain = false;
        const msg = err?.error?.message || err?.message || this.utils.translate('Failed to connect domain. Please check that the domain is valid and registered.');
        this.addDomainError = msg;
        this.utils.notify.showError(msg);
        this.changeDetectorRef.detectChanges();
      }
    );
  }

  showUserInput(domainData: any) {
    if (!domainData) return;
    this.selectedDomain = domainData;

    if (+domainData.setup_progress === 11) {
      if (domainData['resource_record']) {
        this.splitCnameDetails(domainData);
        this.domainModalRef = this.modalService.open(this.guideModal001, {
          size: 'lg',
          backdrop: 'static',
          centered: true,
        });
      } else {
        this.utils.notify.showError(this.utils.translate('Resource record cannot be found. Please try again shortly.'));
      }
    } else if (+domainData.setup_progress === 21) {
      this.stepSubdomainInput = '';
      this.domainModalRef = this.modalService.open(this.guideModal002, {
        size: 'lg',
        backdrop: 'static',
        centered: true,
      });
    } else {
      this.showDomainDetails(domainData);
    }
  }

  splitCnameDetails(domainData: any) {
    try {
      let resourceRecord = domainData['resource_record'] ?? '{}';
      if (typeof resourceRecord === 'string') {
        resourceRecord = JSON.parse(resourceRecord);
      }

      if (!resourceRecord || !resourceRecord.Name) {
        this.selectedDomain['cnameName'] = null;
        this.selectedDomain['cnameValue'] = resourceRecord?.Value ?? null;
        this.selectedDomain['cnameDomainName'] = null;
        return;
      }

      const cnameNameSegments = resourceRecord.Name.split('.');
      const cnameName = cnameNameSegments.shift();
      let cnameDomainName = cnameNameSegments.join('.');
      if (cnameDomainName.endsWith('.')) {
        cnameDomainName = cnameDomainName.slice(0, -1);
      }

      this.selectedDomain['cnameName'] = cnameName;
      this.selectedDomain['cnameValue'] = resourceRecord.Value;
      this.selectedDomain['cnameDomainName'] = cnameDomainName;
    } catch (e) {
      console.error('Error parsing resource record', e);
    }
  }

  cnameSubmitted(selectedDomain: any) {
    if (!selectedDomain) return;
    const params = {
      store_domain_id: selectedDomain.store_domain_id,
      storeId: selectedDomain.store_id || this.store_id,
    };

    const token = localStorage.getItem('bearer') || this.bearer;
    this.isVerifyingCname = true;

    this.svc.requestVerifyDomain(token, params).subscribe(
      (res: any) => {
        this.isVerifyingCname = false;
        if (res && res['status'] === 'success' && res['data']) {
          const check = res['data']['check'];

          if (check && check['found'] === false) {
            const name = check['name'] || selectedDomain?.cnameName || '';
            const target = check['target'] || selectedDomain?.cnameValue || '';
            this.utils.msgBox.open({
              title: this.utils.translate('DNS Record Not Detected Yet'),
              icon: 'warning',
              message: [
                this.utils.translate("We couldn't detect the CNAME record on your domain's DNS yet."),
                '',
                `Host / Name:\n${name}`,
                '',
                `Points to / Value:\n${target}`,
                '',
                this.utils.translate('DNS changes usually take a few minutes to propagate worldwide. If you just added the record, please wait a moment and try again.')
              ].join('\n'),
              buttonList: [
                {
                  text: this.utils.translate("I'll Check My DNS"),
                  style: 'primary',
                },
              ],
            });
            return;
          }

          if (check && check['found'] === true) {
            this.closeDomainModal();
            this.getDomainList();
            this.utils.msgBox.open({
              title: this.utils.translate('Domain Verified'),
              icon: 'success',
              message: [
                this.utils.translate('DNS record found and verified!'),
                '',
                this.utils.translate('Your SSL certificate is being provisioned. Setup will now proceed automatically.')
              ].join('\n'),
              buttonList: [
                {
                  text: this.utils.translate('Continue'),
                  style: 'primary',
                },
              ],
            });
            return;
          }

          this.closeDomainModal();
          this.getDomainList();
        } else if (res && res['status'] === 'error') {
          const msg = this.extractApiErrorMessage(res);
          this.utils.notify.showError(msg);
        } else {
          this.utils.notify.showError(
            this.utils.translate('Verification failed. Please ensure the CNAME DNS record is added and try again.')
          );
        }
        this.changeDetectorRef.detectChanges();
      },
      (error: any) => {
        this.isVerifyingCname = false;
        const api = error?.error;
        if (api && api.status === 'error') {
          const msg = this.extractApiErrorMessage(api);
          this.utils.notify.showError(msg);
        } else {
          this.utils.notify.showError(
            this.utils.translate('Something went wrong while verifying your domain. Please try again.')
          );
        }
        this.changeDetectorRef.detectChanges();
      }
    );
  }

  verifyStep21Subdomain() {
    const sub = (this.stepSubdomainInput || '').trim().toLowerCase();
    if (!sub) {
      this.utils.msgBox.open({
        message: this.utils.translate('Please enter the subdomain you configured (e.g. shop, store, @, or *).'),
        title: this.utils.translate('Subdomain required'),
        icon: 'warning',
        buttonList: [
          {
            text: this.utils.translate('OK'),
            style: 'primary',
          },
        ],
      });
      return;
    }

    const params = {
      store_domain_id: this.selectedDomain.store_domain_id,
      storeId: this.selectedDomain.store_id || this.store_id,
      subdomain: sub,
    };

    const token = localStorage.getItem('bearer') || this.bearer;
    this.isVerifyingStepSubdomain = true;

    this.svc.requestVerifyDomain(token, params).subscribe(
      (res: any) => {
        this.isVerifyingStepSubdomain = false;
        if (res && res['status'] === 'success') {
          const check = res['data']?.['check'];
          if (check && check['found'] === false) {
            const name = check['name'] || sub;
            const target = check['target'] || this.selectedDomain?.cf_dist_domain_name || '';
            this.utils.msgBox.open({
              title: this.utils.translate('Subdomain DNS Record Not Found'),
              icon: 'warning',
              message: [
                this.utils.translate("We couldn't detect the DNS record pointing to CloudFront yet."),
                '',
                `Host / Subdomain:\n${name}`,
                '',
                `Points to / Target:\n${target}`,
                '',
                this.utils.translate('DNS changes may take a few moments to propagate across global networks. Please check your registrar settings and try again.')
              ].join('\n'),
              buttonList: [
                {
                  text: this.utils.translate("I'll Check My DNS"),
                  style: 'primary',
                },
              ],
            });
            return;
          }

          this.utils.notify.showSuccess(
            this.utils.translate("Subdomain '{0}' configured and verified successfully.", [sub])
          );
          this.closeDomainModal();
          this.getDomainList();
        } else if (res && res['status'] === 'error') {
          const msg = this.extractApiErrorMessage(res);
          this.utils.notify.showError(msg);
        } else {
          this.utils.notify.showError(
            res?.message || this.utils.translate('Verification failed. Please check your DNS record.')
          );
        }
        this.changeDetectorRef.detectChanges();
      },
      (err: any) => {
        this.isVerifyingStepSubdomain = false;
        const api = err?.error;
        if (api && api.status === 'error') {
          const msg = this.extractApiErrorMessage(api);
          this.utils.notify.showError(msg);
        } else {
          this.utils.notify.showError(
            err?.error?.message || this.utils.translate('Failed to verify subdomain. Please check your DNS settings.')
          );
        }
        this.changeDetectorRef.detectChanges();
      }
    );
  }

  copyToClipboard(text: string, label: string = 'Text') {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => {
        this.utils.notify.showSuccess(
          this.utils.translate("Copied '{0}' to clipboard.", [label])
        );
      },
      () => {
        this.utils.notify.showError(this.utils.translate('Failed to copy to clipboard.'));
      }
    );
  }

  selectAll(event: any) {
    if (event?.target?.select) {
      event.target.select();
    }
  }

  private extractApiErrorMessage(res: any): string {
    const rawMsg = res?.message;
    if (typeof rawMsg === 'string' && rawMsg.trim()) {
      return rawMsg;
    }
    const nestedMsg = res?.message?.subdomain || res?.data?.subdomain;
    if (typeof nestedMsg === 'string' && nestedMsg.trim()) {
      return nestedMsg;
    }
    return this.utils.translate('Verification failed. Please try again later.');
  }
}
